import { Check, Clock3, Sparkles } from 'lucide-react';

import type { SnapshotGoal } from 'services/api';

export type GoalCelebrationKind = SnapshotGoal['progress']['kind'];

export interface QueueCompletionSummary {
  updated: number;
  skipped: number;
}

const pluralize = (value: number, singular: string) => `${singular}${value === 1 ? '' : 's'}`;

const targetHeadline = (goal: SnapshotGoal) => {
  const { progress } = goal;
  if (progress.kind === 'recurring') {
    const period = pluralize(progress.required_periods, progress.period_unit);
    return `${progress.required_periods} ${period} of intentional consistency.`;
  }
  if (progress.kind === 'count' || progress.kind === 'quantity') {
    return `${progress.target} ${progress.unit} completed.`;
  }
  if (progress.kind === 'milestone') {
    return `${progress.steps.length} ${pluralize(progress.steps.length, 'milestone')} completed.`;
  }
  if (progress.kind === 'deadline') {
    return `${progress.steps.length} ${pluralize(progress.steps.length, 'step')} completed.`;
  }
  if (progress.kind === 'reflective' && progress.target) {
    return `${progress.target} ${pluralize(progress.target, 'reflection')} completed.`;
  }
  return 'You followed through on what mattered.';
};

interface GoalTargetCelebrationProps {
  goal: SnapshotGoal;
  error: string;
  saving: boolean;
  onKeepGoing: () => void;
  onMarkComplete: () => void;
}

export const GoalTargetCelebration = ({
  goal,
  error,
  saving,
  onKeepGoing,
  onMarkComplete,
}: GoalTargetCelebrationProps) => (
  <div className={`goal-celebration goal-celebration--target goal-celebration--${goal.progress.kind}`}>
    <div className="goal-celebration__cairn-scene" data-testid="goal-completion-cairn" aria-hidden="true">
      <svg className="goal-celebration__cairn" viewBox="0 0 322 152" role="presentation" focusable="false">
        <path className="goal-cairn__track" d="M28 139 C 74 137, 106 133, 150 131" />
        <path className="goal-cairn__track" d="M294 139 C 248 137, 216 133, 172 131" />
        <ellipse className="goal-cairn__groundshade" cx="161" cy="141" rx="72" ry="7" fill="#E6DED2" />
        <circle className="goal-cairn__halo" cx="161" cy="96" r="66" />
        <g className="goal-cairn__stone goal-cairn__stone--base" data-testid="goal-cairn-stone">
          <path d="M106 126 C106 111 129 104 161 104 C193 104 215 112 215 127 C215 138 190 143 159 143 C127 143 106 138 106 126 Z" fill="var(--goal-cairn-sage)" />
          <path d="M108 130 C124 139 196 140 214 129 C213 139 190 143 159 143 C127 143 108 139 108 130 Z" fill="var(--goal-cairn-sage-dark)" opacity=".55" />
        </g>
        <g className="goal-cairn__stone goal-cairn__stone--middle" data-testid="goal-cairn-stone">
          <path d="M120 96 C120 84 139 78 164 78 C189 78 205 85 204 98 C203 108 184 113 159 112 C134 111 120 106 120 96 Z" fill="var(--goal-cairn-blue)" />
          <path d="M121 100 C136 108 192 109 204 99 C203 109 184 113 159 112 C134 111 121 108 121 100 Z" fill="var(--goal-cairn-blue-dark)" opacity=".6" />
        </g>
        <g className="goal-cairn__stone goal-cairn__stone--top" data-testid="goal-cairn-stone">
          <path d="M131 72 C131 62 146 56 164 57 C182 58 194 64 193 74 C192 83 177 87 160 86 C143 85 131 81 131 72 Z" fill="var(--goal-cairn-green)" />
          <path d="M132 76 C144 83 183 84 193 75 C192 84 177 87 160 86 C143 85 132 82 132 76 Z" fill="#255C3E" opacity=".7" />
        </g>
        <g className="goal-cairn__capstone" data-testid="goal-cairn-capstone">
          <path d="M143 50 C143 41 151 35 162 35 C173 35 181 41 181 50 C181 58 173 63 162 63 C151 63 143 58 143 50 Z" fill="var(--foundation-orange-500)" />
          <path d="M144 54 C152 60 174 60 181 53 C180 59 173 63 162 63 C151 63 144 59 144 54 Z" fill="var(--foundation-orange-900)" opacity=".22" />
        </g>
        <g className="goal-cairn__check" data-testid="goal-cairn-check">
          <circle cx="162" cy="17" r="12" fill="var(--goal-cairn-success)" />
          <path d="M156.5 17.2 l4 4 l7 -7.6" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      </svg>
    </div>

    <div className="goal-celebration__copy">
      <span className="goal-checkin__eyebrow">Target reached</span>
      <h2 className="goal-celebration__title">You reached your goal</h2>
      <p className="goal-celebration__headline">{targetHeadline(goal)}</p>
      <p className="goal-celebration__goal-title">{goal.title}</p>
    </div>

    {error ? <p className="goal-checkin__error-detail" role="alert">{error}</p> : null}

    <div className="goal-celebration__actions">
      <button type="button" className="goal-checkin__primary" disabled={saving} onClick={onMarkComplete}>
        {saving ? 'Marking complete…' : 'Mark goal complete'}
      </button>
      <button type="button" className="goal-checkin__quiet-action" disabled={saving} onClick={onKeepGoing}>
        Keep tracking
      </button>
    </div>
  </div>
);

export const QueueCompletionCelebration = ({ updated, skipped }: QueueCompletionSummary) => {
  const allSkipped = updated === 0;
  const queueSize = Math.max(updated + skipped, 1);
  const title = allSkipped ? 'Paused for now' : skipped > 0 ? 'Check-in complete' : 'You’re caught up';
  const updatedCopy = updated ? `${updated} ${updated === 1 ? 'goal' : 'goals'} updated` : '';
  const skippedCopy = skipped ? `${skipped} skipped until tomorrow` : '';
  const summary = [updatedCopy, skippedCopy].filter(Boolean).join(' · ');

  return (
    <div className={`goal-celebration goal-celebration--queue ${allSkipped ? 'goal-celebration--queue-neutral' : ''}`} role="status" aria-live="polite">
      <div className="goal-celebration__queue-mark" aria-hidden="true">
        <div className="goal-celebration__queue-segments">
          {Array.from({ length: queueSize }, (_, index) => (
            <span
              key={index}
              data-testid="goal-celebration-segment"
              style={{ animationDelay: allSkipped ? '1000ms' : `${index * 160}ms, 1000ms` }}
            />
          ))}
        </div>
        {allSkipped ? (
          <span className="goal-celebration__queue-clock" data-testid="goal-celebration-neutral-mark"><Clock3 size={27} strokeWidth={2.3} /></span>
        ) : (
          <>
            <span className="goal-celebration__queue-check" data-testid="goal-celebration-success-mark"><Check size={28} strokeWidth={2.8} /></span>
            <Sparkles className="goal-celebration__queue-spark" size={18} />
          </>
        )}
      </div>
      <div className="goal-celebration__queue-copy">
        <span className="goal-checkin__eyebrow">Daily goal check-in</span>
        <h2 className="goal-celebration__title">{title}</h2>
        <p className="goal-celebration__headline">{summary}.</p>
        <p className="goal-celebration__closing">Small steps still count. Updated goals return tomorrow; completed goals stay safely in your history.</p>
      </div>
    </div>
  );
};
